import * as d3 from 'd3'
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core'
import {
    FloorTableViewModel,
} from '@app/features/shared/components/area-floor/floor-table.view-model'
import { Subject, takeUntil } from 'rxjs'
import { TableShape } from '@app/domain/TableShape'
import { CalendarBookingViewModel } from '@app/features/bookings/components/booking-graph/view-models/calendar-booking-view-model'
import { Venue } from '@app/domain/Venue'
import { Booking } from '@app/domain/Booking'
import { ServiceStatus } from '@app/domain/ServiceStatus'
import { BookingStatusType } from '@app/domain/BookingStatus'

type ChairSide = 'top' | 'right' | 'bottom' | 'left'

@Component({
    selector: 'app-floor-table',
    templateUrl: './floor-table.component.html',
    styleUrls: ['./floor-table.component.sass'],
})
export class FloorTableComponent implements OnInit, OnChanges, OnDestroy {

    @Input() tableViewModel!: FloorTableViewModel
    @Input() xScale!: d3.ScaleLinear<number, number>
    @Input() yScale!: d3.ScaleLinear<number, number>
    @Input() allTables: FloorTableViewModel[] = []
    @Input() venue?: Venue
    @Input() activeEdges: { left: boolean; right: boolean; top: boolean; bottom: boolean } | null = null
    @Input() isDragging = false
    @Output() bookingHover = new EventEmitter<string | null>()

    bookedStatus = ''
    shapeClass = ''
    editModeClass = ''
    roundTableBorderRadius = '50%'
    chairSides: { top: number; right: number; bottom: number; left: number } = { top: 0, right: 0, bottom: 0, left: 0 }
    chairArrayPerSide: { [K in ChairSide]: any[] } = { top: [], right: [], bottom: [], left: [] }
    roundTableChairs: { angle: number; x: number; y: number }[] = []
    readonly maxRenderableChairs = 8
    readonly minChairPixelSize = 10 // width / height min (diameter of full circle before clipping)
    bookingTooltip = '' // legacy fallback if view model construction not possible
    bookingTooltipDisabled = false
    calendarBookingTooltipViewModel: CalendarBookingViewModel | null = null
    bookingProgress = 0 // 0..1 fraction of current active booking elapsed
    activeServiceStatus: ServiceStatus | null = null
    private progressTimer: any
    private destroy$ = new Subject<void>()

    ngOnInit() {
        if (this.tableViewModel.dimensions$ && this.tableViewModel.table.shape === TableShape.Round) {
            this.tableViewModel.dimensions$
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                    this.calculateRoundTableBorderRadius()
                })
        }
    this.startProgressTimer()
    }

    ngOnDestroy() {
        this.destroy$.next()
        this.destroy$.complete()
        if (this.progressTimer) {
            clearInterval(this.progressTimer)
        }
    }

    ngOnChanges() {
        if (this.tableViewModel.bookingStatusClass) {
            this.bookedStatus = this.tableViewModel.bookingStatusClass
        } else if (this.tableViewModel.isBooked) {
            this.bookedStatus = 'booked'
        } else if (this.tableViewModel.isSeated) {
            this.bookedStatus = 'seated'
        } else if (this.tableViewModel.isDisabled) {
            this.bookedStatus = 'disabled'
        } else if (this.tableViewModel.isSelected) {
            this.bookedStatus = 'selected'
        } else {
            this.bookedStatus = ''
        }

        this.shapeClass = this.tableViewModel.table.shape === TableShape.Round ? 'round-table' : ''
        this.editModeClass = this.tableViewModel.canUpdateCoordinates ? 'edit-mode' : ''
        
        if (this.tableViewModel.table.shape === TableShape.Round) {
            this.calculateRoundTableBorderRadius()
        }

    this.distributeChairs()
    this.bookingTooltipDisabled = 'ontouchstart' in document.documentElement
    this.bookingTooltip = this.makeBookingTooltip()
    this.calendarBookingTooltipViewModel = this.makeCalendarBookingTooltipViewModel()
    this.updateActiveServiceStatus()
    this.updateBookingProgress()
    }

    onMouseEnterTable() {
        const booking = this.getActiveBooking()
        if (booking) {
            this.bookingHover.emit(booking.id)
        }
    }

    onMouseLeaveTable() {
        this.bookingHover.emit(null)
    }

    private getActiveBooking(): Booking | null {
        return this.tableViewModel.activeBooking()
    }

    private calculateRoundTableBorderRadius() {
        const scaledWidth = this.tableViewModel.dimensions.width * this.xScale(1)
        const scaledHeight = this.tableViewModel.dimensions.height * this.yScale(1)
        const cornerRadiusDistance = Math.min(scaledWidth, scaledHeight)
        this.roundTableBorderRadius = `${cornerRadiusDistance}px`
    }

    private distributeChairs() {
        const capacity = Math.min(this.tableViewModel.table.maximumSeats, this.maxRenderableChairs)
        if (!capacity || capacity <= 0) { this.resetChairs(); return }

        if (this.tableViewModel.table.shape === TableShape.Round) {
            this.distributeChairsRadially(capacity)
            return
        }

        const allSides: ChairSide[] = ['top', 'bottom', 'left', 'right']
        const blocked = this.detectAdjacentBlockedSides()
        // Allowed sides are those not blocked; if all blocked, allow all.
        let allowed = allSides.filter(s => !blocked.has(s))
        if (allowed.length === 0) { allowed = [...allSides] }

        const { width, height } = this.tableViewModel.dimensions
        const aspectRatio = width / height
        const isHorizontallyElongated = aspectRatio > 1.5
        const isVerticallyElongated = aspectRatio < 0.67

        let allocation: { [k in ChairSide]: number } = { top:0,bottom:0,left:0,right:0 }

        if (isHorizontallyElongated) {
            allocation = this.distributeForElongatedTable(capacity, allowed, 'top', 'bottom', 'left', 'right')
        } else if (isVerticallyElongated) {
            allocation = this.distributeForElongatedTable(capacity, allowed, 'left', 'right', 'top', 'bottom')
        } else {
            allocation = this.distributeForSquareTable(capacity, allowed)
        }

        // Zero out blocked sides and redistribute
        let toRedistribute = 0
        for (const side of allSides) {
            if (!allowed.includes(side)) { 
                toRedistribute += allocation[side]
                allocation[side] = 0
            }
        }
        if (toRedistribute > 0) {
            const preferredSides = allowed.filter(s => allocation[s] > 0).sort((a, b) => allocation[b] - allocation[a])
            const fallbackSides = allowed.filter(s => allocation[s] === 0)
            const redistributionOrder = [...preferredSides, ...fallbackSides]
            
            let i = 0
            while (toRedistribute > 0) {
                const side = redistributionOrder[i % redistributionOrder.length]
                allocation[side]++
                toRedistribute--
                i++
            }
        }

        this.chairSides = allocation
        this.chairArrayPerSide = {
            top: new Array(allocation.top),
            right: new Array(allocation.right),
            bottom: new Array(allocation.bottom),
            left: new Array(allocation.left),
        }
    }

    private distributeForElongatedTable(
        capacity: number,
        allowed: ChairSide[],
        primarySide1: ChairSide,
        primarySide2: ChairSide,
        secondarySide1: ChairSide,
        secondarySide2: ChairSide
    ): { [k in ChairSide]: number } {
        const allocation: { [k in ChairSide]: number } = { top:0,bottom:0,left:0,right:0 }
        const hasPrimary1 = allowed.includes(primarySide1)
        const hasPrimary2 = allowed.includes(primarySide2)
        const hasSecondary1 = allowed.includes(secondarySide1)
        const hasSecondary2 = allowed.includes(secondarySide2)

        if (capacity <= 4) {
            // For small capacity, try to use only primary sides symmetrically
            if (hasPrimary1 && hasPrimary2) {
                const perSide = Math.floor(capacity / 2)
                allocation[primarySide1] = perSide
                allocation[primarySide2] = perSide
                // If odd number, add the extra to first primary side
                const remainder = capacity % 2
                if (remainder > 0) {
                    allocation[primarySide1] += remainder
                }
            } else if (hasPrimary1 || hasPrimary2) {
                const primarySide = hasPrimary1 ? primarySide1 : primarySide2
                allocation[primarySide] = capacity
            } else {
                // No primary sides available, use secondary symmetrically
                if (hasSecondary1 && hasSecondary2) {
                    const perSide = Math.floor(capacity / 2)
                    allocation[secondarySide1] = perSide
                    allocation[secondarySide2] = perSide
                    if (capacity % 2 > 0) {
                        allocation[secondarySide1] += 1
                    }
                } else {
                    allocation[allowed[0]] = capacity
                }
            }
        } else {
            // For larger capacity, prioritise primary sides and keep them symmetric
            if (hasPrimary1 && hasPrimary2) {
                // Calculate how many chairs for primary pair (always even distribution)
                const perPrimary = Math.floor(capacity / 2)
                allocation[primarySide1] = perPrimary
                allocation[primarySide2] = perPrimary
                
                // Any odd remainder goes to secondary sides
                const remainder = capacity % 2
                if (remainder > 0) {
                    if (hasSecondary1) {
                        allocation[secondarySide1] = 1
                    } else if (hasSecondary2) {
                        allocation[secondarySide2] = 1
                    } else {
                        // No secondary sides, add to first primary
                        allocation[primarySide1] += 1
                    }
                }
            } else if (hasPrimary1 || hasPrimary2) {
                const primarySide = hasPrimary1 ? primarySide1 : primarySide2
                allocation[primarySide] = capacity
            } else {
                // No primary sides available, use secondary symmetrically
                if (hasSecondary1 && hasSecondary2) {
                    const perSide = Math.floor(capacity / 2)
                    allocation[secondarySide1] = perSide
                    allocation[secondarySide2] = perSide
                    if (capacity % 2 > 0) {
                        allocation[secondarySide1] += 1
                    }
                } else if (hasSecondary1) {
                    allocation[secondarySide1] = capacity
                } else if (hasSecondary2) {
                    allocation[secondarySide2] = capacity
                } else {
                    allocation[allowed[0]] = capacity
                }
            }
        }

        return allocation
    }

    private distributeForSquareTable(
        capacity: number,
        allowed: ChairSide[]
    ): { [k in ChairSide]: number } {
        const allocation: { [k in ChairSide]: number } = { top:0,bottom:0,left:0,right:0 }
        
        // Distribute evenly across all allowed sides, maintaining symmetry on opposite pairs
        const hasTop = allowed.includes('top')
        const hasBottom = allowed.includes('bottom')
        const hasLeft = allowed.includes('left')
        const hasRight = allowed.includes('right')

        if (allowed.length === 4) {
            const perSide = Math.floor(capacity / 4)
            const remainder = capacity % 4
            
            allocation.top = perSide
            allocation.bottom = perSide
            allocation.left = perSide
            allocation.right = perSide

            // Distribute remainder symmetrically: prioritise opposite pairs
            if (remainder >= 1) allocation.top += 1
            if (remainder >= 2) allocation.bottom += 1
            if (remainder >= 3) allocation.left += 1
        } else if (allowed.length === 3) {
            // Three sides - check if we have a complete opposite pair
            if (hasTop && hasBottom) {
                // Top/Bottom pair + one other side
                const pairSide = hasLeft ? 'left' : 'right'
                const pairTotal = Math.floor(capacity * 0.67) // Give more to the pair
                const perPairSide = Math.floor(pairTotal / 2)
                allocation.top = perPairSide
                allocation.bottom = perPairSide
                allocation[pairSide] = capacity - (perPairSide * 2)
                // Balance the pair if odd
                if (pairTotal % 2 > 0) {
                    allocation.top += 1
                }
            } else if (hasLeft && hasRight) {
                // Left/Right pair + one other side
                const pairSide = hasTop ? 'top' : 'bottom'
                const pairTotal = Math.floor(capacity * 0.67)
                const perPairSide = Math.floor(pairTotal / 2)
                allocation.left = perPairSide
                allocation.right = perPairSide
                allocation[pairSide] = capacity - (perPairSide * 2)
                if (pairTotal % 2 > 0) {
                    allocation.left += 1
                }
            } else {
                // No complete pair - distribute evenly
                const perSide = Math.floor(capacity / 3)
                const remainder = capacity % 3
                let idx = 0
                for (const side of allowed) {
                    allocation[side] = perSide + (idx < remainder ? 1 : 0)
                    idx++
                }
            }
        } else if (allowed.length === 2) {
            // Two sides - always distribute symmetrically
            const perSide = Math.floor(capacity / 2)
            allocation[allowed[0]] = perSide
            allocation[allowed[1]] = perSide
            // Add remainder to first side
            if (capacity % 2 > 0) {
                allocation[allowed[0]] += 1
            }
        } else {
            // Only one side available
            allocation[allowed[0]] = capacity
        }

        return allocation
    }

    private detectAdjacentBlockedSides(): Set<ChairSide> {
        const blocked = new Set<ChairSide>()
        if (!this.allTables || this.allTables.length <= 1) { return blocked }
        // Only consider directly touching (gap == 0) as adjacent
        const buffer = 0
        const { coordinates, dimensions } = this.tableViewModel
        const left = coordinates.x
        const right = coordinates.x + dimensions.width
        const top = coordinates.y
        const bottom = coordinates.y + dimensions.height

        for (const other of this.allTables) {
            if (other === this.tableViewModel) continue
            const oLeft = other.coordinates.x
            const oRight = other.coordinates.x + other.dimensions.width
            const oTop = other.coordinates.y
            const oBottom = other.coordinates.y + other.dimensions.height
            const horizontalOverlap = !(oRight <= left || oLeft >= right)
            const verticalOverlap = !(oBottom <= top || oTop >= bottom)

            // Top side blocked if another table vertically adjacent above within buffer and horizontally overlaps
            if (horizontalOverlap && (top - oBottom) === buffer) {
                blocked.add('top')
            }
            // Bottom side
            if (horizontalOverlap && (oTop - bottom) === buffer) {
                blocked.add('bottom')
            }
            // Left side
            if (verticalOverlap && (left - oRight) === buffer) {
                blocked.add('left')
            }
            // Right side
            if (verticalOverlap && (oLeft - right) === buffer) {
                blocked.add('right')
            }
            if (blocked.size === 4) break
        }
        return blocked
    }

    private distributeChairsRadially(capacity: number) {
        this.roundTableChairs = []
        const angleStep = 360 / capacity
        const startAngle = -90 // Start at top (12 o'clock position)
        
        for (let i = 0; i < capacity; i++) {
            const angle = startAngle + (i * angleStep) + 90 // Add 90° so width points to center
            const radians = ((startAngle + (i * angleStep)) * Math.PI) / 180
            
            // Position chairs at 50% radius (edge of table)
            const x = 50 + Math.cos(radians) * 50
            const y = 50 + Math.sin(radians) * 50
            
            this.roundTableChairs.push({ angle, x, y })
        }
        
        // Clear regular chair sides for round tables (but keep roundTableChairs)
        this.chairSides = { top: 0, right: 0, bottom: 0, left: 0 }
        this.chairArrayPerSide = { top: [], right: [], bottom: [], left: [] }
    }

    private resetChairs() {
        this.chairSides = { top: 0, right: 0, bottom: 0, left: 0 }
        this.chairArrayPerSide = { top: [], right: [], bottom: [], left: [] }
        this.roundTableChairs = []
    }

    private makeBookingTooltip(): string {
        // Only for diary floor plan view model instances that expose activeBooking()
        const anyVm: any = this.tableViewModel as any
        if (!anyVm.activeBooking || typeof anyVm.activeBooking !== 'function') return ''
        const booking = anyVm.activeBooking()
        if (!booking) return ''
        // Basic details: name / party size / time range / status
        const name = booking.customerName || 'Booking'
        const party = booking.partySize ? `(${booking.partySize})` : ''
        const start = booking.start ? new Date(booking.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''
        const end = booking.end ? new Date(booking.end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''
        const status = booking.status ? booking.status().type : ''
        return `${name} ${party}\n${start} - ${end}\n${status}`.trim()
    }

    private makeCalendarBookingTooltipViewModel(): CalendarBookingViewModel | null {
        const booking = this.tableViewModel.activeBooking()
        if (!booking || !this.venue) {
            return null
        }
        const area = this.venue.areaWithTableId(this.tableViewModel.table.id)
        if (!area) {
            return null
        }
        const now = new Date()
        try {
            return new CalendarBookingViewModel(
                booking,
                now,
                area,
                this.venue,
                false
            )
        } catch {
            return null
        }
    }

    private startProgressTimer() {
        this.progressTimer = setInterval(() => {
            this.updateBookingProgress()
        }, 60_000)
        this.updateBookingProgress()
    }

    private updateBookingProgress() {
        const booking = this.tableViewModel.activeBooking()
        if (!booking) {
            this.bookingProgress = 0
            this.updateActiveServiceStatus()
            // Update booking status when no active booking
            if (this.tableViewModel.isDisabled) {
                this.bookedStatus = 'disabled'
            } else if (this.tableViewModel.isSelected) {
                this.bookedStatus = 'selected'
            } else {
                this.bookedStatus = ''
            }
            return
        }
        
        // Update booking status class based on current active booking
        if (this.tableViewModel.bookingStatusClass) {
            this.bookedStatus = this.tableViewModel.bookingStatusClass
        } else if (this.tableViewModel.isBooked) {
            this.bookedStatus = 'booked'
        } else if (this.tableViewModel.isSeated) {
            this.bookedStatus = 'seated'
        } else {
            this.bookedStatus = ''
        }
        
        const start = booking.start?.getTime?.() ?? null
        const end = booking.end?.getTime?.() ?? null
        if (!start || !end || end <= start) {
            this.bookingProgress = 0
            this.updateActiveServiceStatus()
            return
        }
        const now = Date.now()
        const pct = (now - start) / (end - start)
        this.bookingProgress = Math.min(1, Math.max(0, pct))
        this.updateActiveServiceStatus()
    }

    private updateActiveServiceStatus() {
        if (!this.venue?.serviceStatusesEnabled) {
            this.activeServiceStatus = null
            return
        }

        const booking = this.tableViewModel.activeBooking()
        if (!booking) {
            this.activeServiceStatus = null
            return
        }
        const isSeated = booking.status().type === BookingStatusType.Seated
        const serviceStatus = booking.serviceStatus
        const isServiceStatusEnabled = serviceStatus && this.venue.serviceStatusIsEnabled(serviceStatus)
        this.activeServiceStatus = isSeated && isServiceStatusEnabled ? serviceStatus : null
    }
}